import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { Target, Zap, HeartPulse, ArrowRight, ArrowLeft, ShieldAlert, ExternalLink } from "lucide-react";
import { Logo } from "@/components/Logo";

const GOALS = [
  { key: "perte_poids", label: "Perte de poids", icon: Target, testid: "questionnaire-goal-card-weightloss" },
  { key: "prise_muscle", label: "Prise de muscle", icon: Zap, testid: "questionnaire-goal-card-muscle" },
  { key: "remise_forme", label: "Remise en forme", icon: HeartPulse, testid: "questionnaire-goal-card-fitness" },
];

const DRAFT_KEY = "pousse_questionnaire";
const PENDING_KEY = "pousse_pending_onboarding";
const DEFAULT_FORM = {
  goal: "", level: "debutant", date_of_birth: "", gender: "", height_cm: "", weight_kg: "",
  target_weight_kg: "", activity_frequency: "modere", available_equipment: "", location: "Maison",
  dietary_preferences: "", allergies: "", comments: "", pregnancy: false, pathology: false, major_injury: false, consent_data: false,
};
const loadDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || null; } catch (e) { return null; } };

const computeAge = (dob) => {
  if (!dob) return null;
  const b = new Date(dob);
  if (isNaN(b.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a--;
  return a;
};

export default function Questionnaire() {
  const navigate = useNavigate();
  const { user, checkAuth, register } = useAuth();
  const needsAccount = !user;
  const draft = loadDraft();
  const [step, setStep] = useState(draft?.step ?? 0);
  const [blocked, setBlocked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(draft?.f ?? { ...DEFAULT_FORM });
  const [acct, setAcct] = useState({ name: "", email: "", password: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setA = (k) => (e) => setAcct((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, f }));
  }, [step, f]);

  useEffect(() => {
    if (blocked) localStorage.removeItem(DRAFT_KEY);
  }, [blocked]);

  const steps = needsAccount
    ? ["Objectif", "Profil", "Préférences", "Sécurité", "Compte"]
    : ["Objectif", "Profil", "Préférences", "Sécurité"];
  const lastStep = steps.length - 1;

  const canNext = () => {
    if (step === 0) return !!f.goal;
    if (step === 1) return f.date_of_birth && f.height_cm && f.weight_kg;
    return true;
  };

  const buildPayload = () => ({
    ...f, type: "initial",
    height_cm: parseFloat(f.height_cm), weight_kg: parseFloat(f.weight_kg),
    target_weight_kg: f.target_weight_kg ? parseFloat(f.target_weight_kg) : null,
  });

  // Runs after the account exists (email/password just created, or already logged in).
  const finalizeWithAccount = async () => {
    const { data } = await apiClient.post("/questionnaire", buildPayload());
    if (data.health_flag === "blocked_health") { setBlocked("health"); return; }
    if (data.health_flag === "blocked_age" || !data.is_adult) { setBlocked("age"); return; }
    try {
      await apiClient.post("/programs/generate");
    } catch (err) {
      if (err.response?.status === 402) { await checkAuth(); localStorage.removeItem(DRAFT_KEY); navigate("/pricing"); return; }
    }
    await checkAuth();
    localStorage.removeItem(DRAFT_KEY);
    toast.success("Ton essai gratuit démarre 🌱");
    navigate("/programme");
  };

  // Security-step validation done fully client-side, BEFORE any account is created.
  const passSecurityChecks = () => {
    if (!f.consent_data) { toast.error("Le consentement est requis pour continuer."); return false; }
    const age = computeAge(f.date_of_birth);
    if (age !== null && age < 18) { setBlocked("age"); return false; }
    if (f.pregnancy || f.pathology || f.major_injury) { setBlocked("health"); return false; }
    return true;
  };

  const submitEmailAccount = async () => {
    if (!acct.name || !acct.email || acct.password.length < 6) {
      toast.error("Renseigne ton prénom, ton email et un mot de passe (6 caractères min.).");
      return;
    }
    setBusy(true);
    try {
      await register({ name: acct.name, email: acct.email, password: acct.password, date_of_birth: f.date_of_birth });
      await finalizeWithAccount();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const handleGoogle = () => {
    if (!passSecurityChecks()) return;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, f }));
    localStorage.setItem(PENDING_KEY, "1");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePrimary = async () => {
    if (step < 3) {
      if (canNext()) setStep((s) => s + 1); else toast.error("Complète les champs requis.");
      return;
    }
    if (step === 3) {
      if (!passSecurityChecks()) return;
      if (needsAccount) { setStep(4); return; }
      // already logged in → finalize directly
      setBusy(true);
      try { await finalizeWithAccount(); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      finally { setBusy(false); }
      return;
    }
    // step 4 (account)
    await submitEmailAccount();
  };

  if (blocked) {
    return (
      <div className="min-h-screen bg-[#0A0F0D] text-white flex items-center justify-center px-6">
        <div data-testid="health-block-notice" className="max-w-md w-full rounded-2xl border border-red-500/60 bg-red-500/20 p-6 text-center animate-float-up">
          <div className="h-14 w-14 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="h-7 w-7 text-red-400" />
          </div>
          <h2 className="font-display text-xl font-bold mb-3">Prenons soin de toi avant tout</h2>
          <p className="text-sm text-white leading-relaxed mb-4">
            {blocked === "age"
              ? "Pousse est réservé aux personnes de 18 ans et plus. Nous ne pouvons pas générer de programme pour le moment."
              : "Compte tenu des informations que tu as indiquées, nous préférons ne pas générer de programme. Nous t'invitons à consulter un professionnel de santé qui pourra t'accompagner en toute sécurité."}
          </p>
          <button data-testid="block-back-button" onClick={() => navigate("/")} className="text-lime-400 font-semibold text-sm">Retour à l'accueil</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F0D] text-white px-5 py-8">
      <div className="max-w-md mx-auto">
        <div className="mb-6">
          <Logo size="sm" />
        </div>

        <div className="flex gap-1.5 mb-2">
          {steps.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-lime-400" : "bg-white/10"}`} />
          ))}
        </div>
        <p className="text-xs text-white/40 mb-6">Étape {step + 1}/{steps.length} · {steps[step]}</p>

        <div data-testid="questionnaire-step-container" className="animate-float-up">
          {step === 0 && (
            <>
              <h2 className="font-display text-2xl font-bold mb-5">Quel est ton objectif ?</h2>
              <div className="space-y-3">
                {GOALS.map((g) => (
                  <button key={g.key} data-testid={g.testid} onClick={() => set("goal", g.key)}
                    className={`w-full flex items-center gap-4 rounded-2xl p-4 border text-left transition-all ${
                      f.goal === g.key ? "border-lime-400 bg-lime-400/10" : "border-white/10 bg-[#141C17] hover:border-white/25"}`}>
                    <div className="h-11 w-11 rounded-xl bg-lime-400/15 flex items-center justify-center">
                      <g.icon className="h-5 w-5 text-lime-400" />
                    </div>
                    <span className="font-semibold">{g.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h2 className="font-display text-2xl font-bold mb-5">Parle-nous de toi</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-white/50 ml-1">Date de naissance</label>
                  <input data-testid="questionnaire-age-input" type="date" value={f.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50 mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" placeholder="Taille (cm)" value={f.height_cm} onChange={(e) => set("height_cm", e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />
                  <input type="number" placeholder="Poids (kg)" value={f.weight_kg} onChange={(e) => set("weight_kg", e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />
                </div>
                <input type="number" placeholder="Poids cible (kg, optionnel)" value={f.target_weight_kg} onChange={(e) => set("target_weight_kg", e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />
                <div>
                  <label className="text-xs text-white/50 ml-1">Niveau</label>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    {[["debutant", "Débutant"], ["intermediaire", "Intermédiaire"]].map(([k, l]) => (
                      <button key={k} onClick={() => set("level", k)}
                        className={`rounded-xl py-3 text-sm font-semibold border ${f.level === k ? "border-lime-400 bg-lime-400/10 text-lime-300" : "border-white/10 bg-[#141C17]"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/50 ml-1">Sexe (pour le calcul des calories)</label>
                  <div className="grid grid-cols-3 gap-3 mt-1">
                    {[["homme", "Homme"], ["femme", "Femme"], ["autre", "Autre"]].map(([k, l]) => (
                      <button key={k} data-testid={`questionnaire-gender-${k}`} onClick={() => set("gender", k)}
                        className={`rounded-xl py-3 text-sm font-semibold border ${f.gender === k ? "border-lime-400 bg-lime-400/10 text-lime-300" : "border-white/10 bg-[#141C17]"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/50 ml-1">Niveau d'activité</label>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    {[["sedentaire", "Sédentaire"], ["leger", "Légère"], ["modere", "Modérée"], ["actif", "Active"]].map(([k, l]) => (
                      <button key={k} data-testid={`questionnaire-activity-${k}`} onClick={() => set("activity_frequency", k)}
                        className={`rounded-xl py-2.5 text-sm font-semibold border ${f.activity_frequency === k ? "border-lime-400 bg-lime-400/10 text-lime-300" : "border-white/10 bg-[#141C17]"}`}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-display text-2xl font-bold mb-5">Tes préférences</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-white/50 ml-1">Où t'entraînes-tu ?</label>
                  <div className="grid grid-cols-3 gap-3 mt-1">
                    {["Maison", "Salle de sport", "Extérieur"].map((l) => (
                      <button key={l} data-testid={`questionnaire-location-${l}`} onClick={() => set("location", l)}
                        className={`rounded-xl py-2.5 text-xs font-semibold border ${f.location === l ? "border-lime-400 bg-lime-400/10 text-lime-300" : "border-white/10 bg-[#141C17]"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <input placeholder="Matériel dispo (ex: haltères, aucun…)" value={f.available_equipment} onChange={(e) => set("available_equipment", e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />
                <input data-testid="questionnaire-diet-input" placeholder="Régime / préférences alimentaires (ex: végétarien)" value={f.dietary_preferences} onChange={(e) => set("dietary_preferences", e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />
                <input data-testid="questionnaire-allergies-input" placeholder="Aliments à exclure / allergies (ex: légumes, lactose)" value={f.allergies} onChange={(e) => set("allergies", e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />
                <textarea data-testid="questionnaire-comments-input" rows={3} placeholder="Commentaires (goûts, contraintes, horaires, objectifs précis…) — pris en compte pour l'entraînement et l'alimentation" value={f.comments} onChange={(e) => set("comments", e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50 resize-none" />
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="font-display text-2xl font-bold mb-5">Sécurité & consentement</h2>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 mb-4 flex gap-2">
                <ShieldAlert className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-200/90 leading-relaxed">Programme bien-être général, ne remplace pas l'avis d'un professionnel de santé.</p>
              </div>
              <p className="text-sm text-white/60 mb-3">Coche si l'une de ces situations te concerne :</p>
              <div className="space-y-2.5 mb-5">
                {[["pregnancy", "Grossesse en cours", "questionnaire-health-pregnancy-checkbox"],
                  ["pathology", "Pathologie / maladie chronique", "questionnaire-health-pathology-checkbox"],
                  ["major_injury", "Blessure importante en cours", "questionnaire-health-injury-checkbox"]].map(([k, l, tid]) => (
                  <label key={k} data-testid={tid} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#141C17] px-4 py-3 cursor-pointer">
                    <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)}
                      className="h-4 w-4 accent-lime-400" />
                    <span className="text-sm">{l}</span>
                  </label>
                ))}
              </div>
              <label data-testid="questionnaire-consent-checkbox" className="flex items-start gap-3 rounded-xl border border-white/10 bg-[#141C17] px-4 py-3 cursor-pointer">
                <input type="checkbox" checked={f.consent_data} onChange={(e) => set("consent_data", e.target.checked)}
                  className="h-4 w-4 accent-lime-400 mt-0.5" />
                <span className="text-xs text-gray-300 leading-relaxed">
                  J'accepte que mes données de poids et de taille soient utilisées pour personnaliser mon programme.
                  <a href="/politique-confidentialite" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-lime-400 inline-flex items-center gap-1 ml-1">
                    Politique de confidentialité <ExternalLink className="h-3 w-3" />
                  </a>
                </span>
              </label>
            </>
          )}

          {step === 4 && (
            <>
              <h2 className="font-display text-2xl font-bold mb-1">Crée ton compte</h2>
              <p className="text-lime-400 text-sm font-semibold mb-1">Ton essai gratuit de 7 jours démarre juste après 🌱</p>
              <p className="text-white/50 text-sm mb-5">La semaine 1 est offerte, sans carte bancaire.</p>

              <button data-testid="auth-google-button" onClick={handleGoogle}
                className="w-full flex items-center justify-center gap-3 rounded-xl bg-white text-[#0A0F0D] font-semibold py-3.5 mb-5 active:scale-95 transition-transform hover:bg-lime-50">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
                Continuer avec Google
              </button>
              <div className="flex items-center gap-3 my-5">
                <div className="h-px flex-1 bg-white/10" /><span className="text-xs text-white/30">ou</span><div className="h-px flex-1 bg-white/10" />
              </div>
              <div className="space-y-3">
                <input data-testid="register-name-input" value={acct.name} onChange={setA("name")}
                  placeholder="Prénom" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />
                <input data-testid="auth-email-input" type="email" value={acct.email} onChange={setA("email")}
                  placeholder="Email" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />
                <input data-testid="auth-password-input" type="password" minLength={6} value={acct.password} onChange={setA("password")}
                  placeholder="Mot de passe (6 caractères min.)" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3 mt-8">
          {step > 0 && (
            <button onClick={() => setStep((s) => s - 1)}
              className="flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 font-semibold hover:bg-white/5">
              <ArrowLeft className="h-4 w-4" /> Retour
            </button>
          )}
          <button
            data-testid={step === lastStep ? "questionnaire-submit-button" : "questionnaire-next-step-button"}
            onClick={handlePrimary} disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 bg-lime-400 text-black font-extrabold py-3 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all disabled:opacity-60">
            {step < 3 ? (<>Continuer <ArrowRight className="h-4 w-4" /></>)
              : step === 3 ? (needsAccount ? (<>Continuer <ArrowRight className="h-4 w-4" /></>) : (busy ? "Génération…" : "Valider & générer"))
              : (busy ? "Création…" : "Créer mon compte & démarrer")}
          </button>
        </div>

        {step === lastStep && needsAccount && (
          <p className="text-center text-xs text-white/40 mt-5">
            Déjà un compte ? <button onClick={() => navigate("/login")} className="text-lime-400 font-semibold">Se connecter</button>
          </p>
        )}
      </div>
    </div>
  );
}
